import { useQuery } from 'react-query';
import type { GetPlaylistParams } from '@jwp/ott-common/types/playlist';
import type { GetMediaParams } from '@jwp/ott-common/types/media';
import type { EntitlementType } from '@jwp/ott-common/types/entitlement';
import GenericEntitlementService from '@jwp/ott-common/src/services/GenericEntitlementService';
import JWPEntitlementService from '@jwp/ott-common/src/services/JWPEntitlementService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { isTruthyCustomParamValue } from '@jwp/ott-common/src/utils/common';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { useAppConfig } from './useAppConfig';

const useContentProtection = <T>(
  type: EntitlementType,
  id: string | undefined,
  callback: (token?: string, drmPolicyId?: string) => Promise<T | undefined>,
  params: GetPlaylistParams | GetMediaParams = {},
  enabled: true,
  placeholderData?: T,
  isDRMContent?: boolean,
  adDeliveryMethod?: string,
  drmSiteId?: string | unknown,
  getAdConfigId?: () => string | unknown,
) => {
  const genericEntitlementService = getModule(GenericEntitlementService);
  const jwpEntitlementService = getModule(JWPEntitlementService);

  const { configId, signingConfig, contentProtection, jwp, urlSigning, custom } = useConfigStore(({ config }) => ({
    configId: config.id,
    signingConfig: config.contentSigningService,
    contentProtection: config.contentProtection,
    jwp: config.integrations.jwp,
    urlSigning: isTruthyCustomParamValue(config?.custom?.urlSigning),
    custom: config.custom || {},
  }));
  const host = signingConfig?.host;
  const apiService = getModule(ApiService);

  const drmPolicyId = isDRMContent ? custom?.drmPolicyId ?? contentProtection?.drm?.defaultPolicyId ?? signingConfig?.drmPolicyId : '';

  const signingEnabled = !!urlSigning || !!host || (!!drmPolicyId && !host);
  const appConfigId = custom.drmConfigId ?? configId;
  const { isAdEnabled } = useAppConfig();

  const { data: token, isLoading } = useQuery(
    ['token', type, id, params],
    async () => {
      if (isDRMContent && adDeliveryMethod === 'ssai' && id && drmPolicyId && isAdEnabled) {
        const drmResponse = await apiService.getDrmSignedToken(id, drmSiteId as string, drmPolicyId as string, getAdConfigId?.() as string);
        return drmResponse.token;
      }
      // if provider is not JWP
      if (!!id && !!host) {
        const accountController = getModule(AccountController);
        const authData = await accountController.getAuthData();
        const { host, drmPolicyId } = signingConfig;

        return genericEntitlementService.getMediaToken(host, id, authData?.jwt, params, drmPolicyId);
      }
      // if provider is JWP
      if (jwp && configId && !!id && signingEnabled) {
        return jwpEntitlementService.getJWPMediaToken(String(appConfigId), String(id));
      }
    },
    { enabled: signingEnabled && enabled && !!id, keepPreviousData: false, staleTime: 15 * 60 * 1000 },
  );

  const queryResult = useQuery<T | undefined>([type, id, params, token], async () => callback(token, String(drmPolicyId)), {
    enabled: !!id && enabled && (!signingEnabled || !!token),
    placeholderData: id ? placeholderData : undefined,
    retry: 2,
    retryDelay: 1000,
    keepPreviousData: false,
  });

  return {
    ...queryResult,
    isLoading: isLoading || queryResult.isLoading,
  };
};

export default useContentProtection;
